import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import { AdminPageLayout, Card } from '../../components';
import PhotoUpload from '../../components/PhotoUpload';
import galleryApi from '../../services/galleryApi';
import { Image, ArrowLeft } from 'lucide-react';
import { Section, Grid, Col, FormActionGroup } from '../../components';
import { useToast } from '../../contexts/ToastContext';

const GalleryUpload = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    active: true
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const handlePhotoSelect = (fileInfo) => {
    setPhotoFile(fileInfo.file);
    setPhotoPreview(fileInfo.preview);
  };

  const handlePhotoRemove = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      showError('Title is required');
      return false;
    }

    if (formData.title.length > 100) {
      showError('Title must be 100 characters or less');
      return false;
    }

    if (formData.description.length > 500) {
      showError('Description must be 500 characters or less');
      return false;
    }

    if (!photoFile) {
      showError('Please select an image to upload');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('file', photoFile);
      formDataToSubmit.append('title', formData.title.trim());
      formDataToSubmit.append('description', formData.description.trim());
      formDataToSubmit.append('active', formData.active);

      await galleryApi.uploadImage(formDataToSubmit);
      showSuccess('Image uploaded successfully');
      navigate('/admin/gallery');
    } catch (err) {
      console.error('Error uploading image:', err);
      showError(err.response?.data?.detail || 'Failed to upload image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/gallery');
  };

  return (
    <AdminPageLayout
      title="ADD GALLERY IMAGE"
      subtitle="Upload a new image to the gallery"
      backPath="/admin/gallery"
      maxWidth="1400px"
    >
      <Section title="IMAGE UPLOAD">
        <Card level={1}>
          <form onSubmit={handleSubmit} style={{ padding: '2rem' }}>
            {/* Photo Upload */}
            <div style={{ marginBottom: '2rem' }}>
              <PhotoUpload
                onPhotoSelect={handlePhotoSelect}
                photoUrl={photoPreview}
                onPhotoRemove={handlePhotoRemove}
                label="Upload Gallery Image"
                hint="JPG, PNG, or WebP • Max 5MB • Recommended: 1600x900px or larger"
                required={true}
              />
            </div>

            {/* Title */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="title" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--color-text)' }}>
                Title <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter image title"
                maxLength={100}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem', display: 'block' }}>
                {formData.title.length}/100 characters
              </span>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="description" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--color-text)' }}>
                Description (Optional)
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter image description or caption"
                maxLength={500}
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  fontFamily: 'inherit'
                }}
              />
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem', display: 'block' }}>
                {formData.description.length}/500 characters • This will be shown as a caption in the carousel
              </span>
            </div>

            {/* Active Status */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontWeight: '500', color: 'var(--color-text)' }}>Make image active immediately</span>
              </label>
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem', display: 'block', marginLeft: '1.625rem' }}>
                Active images will appear in the public gallery carousel
              </span>
            </div>

            {/* Form Actions */}
            <FormActionGroup
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              submitLabel={loading ? 'Uploading...' : 'Upload Image'}
              disabled={loading}
            />
          </form>
        </Card>
      </Section>

      {/* Help Section */}
      <Section title="TIPS FOR GREAT GALLERY IMAGES">
        <Card level={1}>
          <div style={{ padding: '1.5rem' }}>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', lineHeight: '1.8' }}>
              <li>
                <strong>Image Size:</strong> Use high-quality images at least 1600x900px for best results
              </li>
              <li>
                <strong>File Format:</strong> JPG works best for photos, PNG for graphics with text
              </li>
              <li>
                <strong>File Size:</strong> Keep files under 2MB for faster loading (max 5MB)
              </li>
              <li>
                <strong>Titles:</strong> Keep titles short and descriptive (e.g., "Team Victory 2024")
              </li>
              <li>
                <strong>Descriptions:</strong> Add context to help viewers understand the image
              </li>
              <li>
                <strong>Display Order:</strong> Images are automatically ordered. You can reorder them later in the gallery management page
              </li>
            </ul>
          </div>
        </Card>
      </Section>
    </AdminPageLayout>
  );
};

export default GalleryUpload;
